import React from 'react';
import { Flag, Chip, Button, Field, Icon } from './primitives.jsx';

// ============================================================================
// Eliminator (Last Man Standing) — group-stage screen.
// Pick a team to WIN each matchday. No team twice. 2 lives — a draw, loss or
// missed pick costs one. Out at 0. Lives are public.
// ============================================================================

const OUTCOME = {
  win:        { label: 'WON',        color: 'var(--success)' },
  draw:       { label: 'DREW −1',    color: 'var(--warning)' },
  loss:       { label: 'LOST −1',    color: 'var(--danger)' },
  miss:       { label: 'NO PICK −1', color: 'var(--danger)' },
  pending:    { label: 'AWAITING',   color: 'var(--blue)' },
  upcoming:   { label: 'UPCOMING',   color: 'var(--ink-mute)' },
  eliminated: { label: '—',          color: 'var(--ink-faint)' },
};

// Knockouts are sudden death — no lives, so the wording drops the "−1".
const KO_OUTCOME = {
  win:        { label: 'THROUGH',       color: 'var(--success)' },
  loss:       { label: 'OUT',           color: 'var(--danger)' },
  miss:       { label: 'NO PICK · OUT', color: 'var(--danger)' },
  pending:    { label: 'AWAITING',      color: 'var(--blue)' },
  upcoming:   { label: 'UPCOMING',      color: 'var(--ink-mute)' },
  eliminated: { label: '—',             color: 'var(--ink-faint)' },
};

const Hearts = ({ remaining, total }) => (
  <span style={{ fontSize: 20, letterSpacing: 2, lineHeight: 1 }} aria-label={`${remaining} of ${total} lives`}>
    {Array.from({ length: total }, (_, i) => (
      <span key={i} style={{ color: i < remaining ? 'var(--danger)' : 'var(--ink-faint)' }}>
        {i < remaining ? '♥' : '♡'}
      </span>
    ))}
  </span>
);

export const Eliminator = ({ pool, myTeam }) => {
  const [name, setName] = React.useState(myTeam?.assigned_to || '');
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(null);

  const trimmedName = name.trim();
  const ready = trimmedName.length > 0;
  const teamOf = (id) => pool.teams.find((t) => t.id === id);

  const refresh = React.useCallback(() => {
    const q = ready ? `?name=${encodeURIComponent(trimmedName)}` : '';
    fetch(`/api/pool/${pool.id}/eliminator${q}`).then((r) => r.json()).then(setData).catch(() => {});
  }, [pool.id, trimmedName, ready]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const pick = async (round, teamId) => {
    if (!ready) { setError('Type your name first.'); return; }
    setError(''); setSaving(teamId);
    try {
      const res = await fetch(`/api/pool/${pool.id}/eliminator/pick`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmedName, round, teamId }),
      });
      const body = await res.json();
      if (!res.ok) { setError(body.error || 'Could not save that pick.'); return; }
      refresh();
    } catch { setError('Network error.'); }
    finally { setSaving(null); }
  };

  const me = data?.me;
  const current = data?.currentRound;
  const livesStart = data?.livesStart ?? 2;
  const currentRoundDef = data?.rounds?.find((r) => r.key === current);
  const inKo = data?.currentStage === 'ko';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      {/* Rules strip */}
      <div style={{
        background: 'var(--paper-warm)', border: '1.5px solid var(--hairline-strong)',
        borderRadius: 12, padding: '16px 20px', display: 'flex', gap: 20, flexWrap: 'wrap',
        alignItems: 'center', fontSize: 14, color: 'var(--ink-soft)',
      }}>
        <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--blue)' }}>HOW IT WORKS</span>
        <span>Pick one team to <strong>win</strong> each round.</span>
        <span>Can't reuse a team — ever.</span>
        <span><strong>Group: 2 lives</strong> — a draw, loss or missed pick costs one.</span>
        <span><strong>Knockouts: sudden death</strong> — one wrong pick and you're out. Last one standing wins.</span>
      </div>

      {/* Identity */}
      <div style={{ maxWidth: 360 }}>
        <Field label="Playing as" value={name} onChange={setName} placeholder="e.g. Luke"
               hint="Use the name you drew your team with." />
      </div>

      {error && <div style={{ color: 'var(--danger)', fontSize: 14 }}>{error}</div>}

      {/* Your status */}
      {ready && me && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
          padding: '16px 20px', borderRadius: 12,
          background: me.status === 'out' ? 'rgba(214,35,62,0.06)' : 'var(--blue-pale)',
          border: `1.5px solid ${me.status === 'out' ? 'rgba(214,35,62,0.3)' : 'var(--hairline-blue)'}`,
        }}>
          {!inKo && <Hearts remaining={me.lives} total={livesStart} />}
          <Chip tone={me.status === 'out' ? 'danger' : 'success'}>
            {me.status === 'out' ? 'ELIMINATED' : inKo ? 'STILL IN' : 'STILL ALIVE'}
          </Chip>
          {me.status !== 'out' && inKo && (
            <span style={{ fontSize: 14, color: 'var(--danger)', fontFamily: 'var(--font-mono)' }}>sudden death — one wrong pick is out</span>
          )}
          {me.status !== 'out' && !inKo && me.lives === 1 && (
            <span style={{ fontSize: 14, color: 'var(--danger)', fontFamily: 'var(--font-mono)' }}>last life — pick carefully</span>
          )}
        </div>
      )}

      {/* Current matchday picker */}
      {ready && me && me.status !== 'out' && (
        <section>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 22, textTransform: 'uppercase', margin: '0 0 4px' }}>
            {currentRoundDef ? `Your ${currentRoundDef.label} pick` : 'Tournament complete'}
          </h3>
          {!current && <p style={{ color: 'var(--ink-soft)', margin: '0 0 12px' }}>No round open right now.</p>}
          {current && (
            <>
              <p style={{ color: 'var(--ink-soft)', margin: '0 0 14px', fontSize: 14 }}>
                {me.currentPick
                  ? <>You're on <strong>{teamOf(me.currentPick)?.country || me.currentPick}</strong> — tap another team to switch before kickoff.</>
                  : inKo ? 'Tap the team you think goes through.' : 'Tap the team you think wins their match this round.'}
              </p>
              {me.pickable.length === 0 && <p style={{ color: 'var(--ink-mute)' }}>No teams left to pick this round.</p>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
                {me.pickable.map(({ teamId }) => {
                  const t = teamOf(teamId);
                  const selected = me.currentPick === teamId;
                  return (
                    <button key={teamId} onClick={() => pick(current, teamId)} disabled={saving === teamId}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
                        borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                        background: selected ? 'var(--blue)' : 'var(--paper)',
                        color: selected ? 'var(--paper)' : 'var(--ink)',
                        border: `2px solid ${selected ? 'var(--blue)' : 'var(--hairline-strong)'}`,
                        opacity: saving === teamId ? 0.5 : 1,
                      }}>
                      <Flag teamId={teamId} size={28} />
                      <span style={{ fontFamily: 'var(--font-display)', fontSize: 15, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                        {t?.country || teamId}
                      </span>
                      {selected && <Icon name="check" size={16} />}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </section>
      )}

      {/* Your round-by-round */}
      {ready && me && (
        <section>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 10px', color: 'var(--ink-soft)' }}>Your run</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {me.rounds.map((r) => {
              const o = (r.ko ? KO_OUTCOME : OUTCOME)[r.outcome] || OUTCOME.upcoming;
              const def = data.rounds.find((x) => x.key === r.key);
              return (
                <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 8, background: 'var(--paper-warm)', border: '1px solid var(--hairline)' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink-mute)', minWidth: 86 }}>{def?.label || r.key}</span>
                  {r.teamId ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flex: 1 }}>
                      <Flag teamId={r.teamId} size={22} />
                      <span style={{ fontFamily: 'var(--font-display)', fontSize: 15, textTransform: 'uppercase' }}>{teamOf(r.teamId)?.country || r.teamId}</span>
                    </span>
                  ) : <span style={{ flex: 1, color: 'var(--ink-mute)', fontSize: 14 }}>—</span>}
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: o.color, fontWeight: 700 }}>{o.label}</span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Standings */}
      <section>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 10px', color: 'var(--ink-soft)' }}>
          Who's still standing ({(data?.standings || []).filter((s) => s.status === 'alive').length} alive)
        </h3>
        {(!data || data.standings.length === 0) && <p style={{ color: 'var(--ink-mute)' }}>No picks yet — be the first in.</p>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {(data?.standings || []).map((s) => (
            <div key={s.name} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '8px 14px', borderRadius: 8,
              background: 'var(--paper)', border: '1px solid var(--hairline)',
              opacity: s.status === 'out' ? 0.5 : 1,
            }}>
              <span style={{ flex: 1, fontFamily: 'var(--font-display)', fontSize: 16, textTransform: 'uppercase' }}>{s.name}</span>
              {s.status === 'out'
                ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--danger)' }}>{s.outRound ? `OUT · ${s.outRound.toUpperCase()}` : 'OUT'}</span>
                : inKo
                  ? <Chip tone="success">IN</Chip>
                  : <Hearts remaining={s.lives} total={livesStart} />}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
